import * as pdfjsLib from "pdfjs-dist/build/pdf";
import moment from "moment";
import { round2, detectarReversos } from "./plantillasBancos/helpers";
import { seleccionarPlantilla } from "./plantillasBancos";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js";

/**
 * Extrae el texto de un PDF reconstruyendo líneas a partir de las coordenadas
 * de cada fragmento de texto (agrupando por Y y ordenando por X).
 * Devuelve un arreglo de strings, una por línea, en orden de lectura.
 */
export async function extraerLineasDePdf(file) {
  const buffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  const lineas = [];

  for (let numPagina = 1; numPagina <= pdf.numPages; numPagina++) {
    const page = await pdf.getPage(numPagina);
    const content = await page.getTextContent();

    // Agrupa fragmentos por fila con tolerancia basada en la altura del texto:
    // pdfjs reporta Y con pequeñas diferencias de baseline dentro de una misma
    // fila visual; una tolerancia ~= mitad de la altura reconstruye la fila completa.
    const items = content.items
      .filter((it) => it.str?.trim())
      .map((it) => ({
        str: it.str,
        x: it.transform[4],
        y: it.transform[5],
        h: it.height || Math.abs(it.transform[3]) || 10,
      }))
      .sort((a, b) => b.y - a.y);

    const filas = [];
    for (const it of items) {
      const tol = Math.max(2, it.h * 0.5);
      const fila = filas.find((f) => Math.abs(f.y - it.y) <= tol);
      if (fila) {
        fila.items.push(it);
      } else {
        filas.push({ y: it.y, items: [it] });
      }
    }

    for (const fila of filas) {
      const linea = fila.items
        .sort((a, b) => a.x - b.x)
        .map((i) => i.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      if (linea) lineas.push(linea);
    }
  }

  return lineas;
}

export function extraerMovimientosBanco(lineas, anioRef, opciones = {}) {
  const sel = opciones.plantilla
    ? { plantilla: opciones.plantilla, autodetectada: false, porClaveSat: false, conflicto: null }
    : seleccionarPlantilla((lineas || []).join("\n"), null, opciones.claveSatCuenta);

  const movimientos = detectarReversos(
    sel.plantilla.parsear(lineas, anioRef) || [],
    sel.plantilla.reversos
  );
  return {
    movimientos,
    plantilla: sel.plantilla,
    autodetectada: sel.autodetectada,
    porClaveSat: sel.porClaveSat,
    conflicto: sel.conflicto,
  };
}

/**
 * Uso: MensajeError(mensajePdfDeOtroBanco(conflicto, { bancoSatNombre, claveSatCuenta }))
 */
export function mensajePdfDeOtroBanco(conflicto, { bancoSatNombre } = {}) {
  const bancoCuenta =
    (conflicto && conflicto.esperada && conflicto.esperada.nombre) || bancoSatNombre || null;
  const bancoPdf = conflicto && conflicto.detectada ? conflicto.detectada.nombre : null;

  const detalle =
    (bancoPdf
      ? `El estado de cuenta que subiste es de ${bancoPdf}`
      : "El estado de cuenta que subiste es de otro banco") +
    (bancoCuenta ? ` y esta cuenta está configurada como ${bancoCuenta}.` : ".");

  return {
    message: "Este PDF no es compatible con el banco seleccionado",
    description: `${detalle}\n\nElige el banco correspondiente o, si existe alguna duda, favor de comunicarte con tu administrador para la realización de la plantilla.`,
  };
}

/**
 * Extrae los datos de encabezado (período + saldo final) para pre-llenar una
 * conciliación nueva creada a partir del PDF.
 *
 * Devuelve { encabezado: { fechaInicial, fechaFinal, saldoFinal }, plantilla, autodetectada, porClaveSat, conflicto }.
 */
export function extraerEncabezadoBanco(lineas, opciones = {}) {
  const sel = opciones.plantilla
    ? { plantilla: opciones.plantilla, autodetectada: false, porClaveSat: false, conflicto: null }
    : seleccionarPlantilla((lineas || []).join("\n"), null, opciones.claveSatCuenta);

  const encabezado =
    (sel.plantilla.extraerEncabezado ? sel.plantilla.extraerEncabezado(lineas) : null) || {};
  return {
    encabezado,
    plantilla: sel.plantilla,
    autodetectada: sel.autodetectada,
    porClaveSat: sel.porClaveSat,
    conflicto: sel.conflicto,
  };
}

// ---------------------------------------------------------------------------
// Store en memoria para entregar un PDF ya leído a una conciliación recién
// creada ("Crear desde PDF"). Vive mientras dure la sesión SPA.
// Clave: id de conciliación. data: { lineas: string[], plantillaId: string }
// ---------------------------------------------------------------------------
const pdfPendientePorConciliacion = new Map();

export function guardarPdfPendiente(conciliacionId, data) {
  if (conciliacionId == null) return;
  pdfPendientePorConciliacion.set(String(conciliacionId), data);
}

// Devuelve y CONSUME el pendiente (se usa una sola vez al abrir la conciliación).
export function tomarPdfPendiente(conciliacionId) {
  const key = String(conciliacionId);
  const data = pdfPendientePorConciliacion.get(key);
  pdfPendientePorConciliacion.delete(key);
  return data || null;
}


// Historial DURABLE de las relaciones hechas a mano (empates 1 a 1, grupos N a 1
const PREFIJO_RELACIONES = "erp:conciliacion:relacionesManuales:";

export function claveMovimientoPdf(mov) {
  if (!mov) return null;
  const fecha = mov.fecha ? moment(mov.fecha).format("YYYY-MM-DD") : "";
  const monto = round2(mov.monto);
  const concepto = (mov.concepto || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return `${fecha}|${monto}|${mov.tipo || ""}|${concepto}`;
}

export function guardarRelacionesManuales(conciliacionId, data) {
  if (conciliacionId == null) return;
  try {
    window.localStorage.setItem(
      PREFIJO_RELACIONES + String(conciliacionId),
      JSON.stringify({ v: 1, ...data })
    );
  } catch (err) {
  }
}

export function leerRelacionesManuales(conciliacionId) {
  if (conciliacionId == null) return null;
  try {
    const raw = window.localStorage.getItem(PREFIJO_RELACIONES + String(conciliacionId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && data.v === 1 ? data : null;
  } catch (err) {
    return null;
  }
}

export function borrarRelacionesManuales(conciliacionId) {
  if (conciliacionId == null) return;
  try {
    window.localStorage.removeItem(PREFIJO_RELACIONES + String(conciliacionId));
  } catch (err) {
    // ver guardarRelacionesManuales
  }
}

/**
 * Empareja los movimientos del sistema (cargosAbonos) con los candidatos
 * extraídos del PDF.
 *
 * Criterio de empate: mismo monto (con tolerancia), mismo tipo (cargo/abono;
 * si el candidato del PDF trae tipo=null se ignora el tipo) y fecha dentro de
 * una ventana de días. Se elige el sistema con la fecha más cercana y se marca
 * como usado para no emparejarlo dos veces.
 *
 * Devuelve:
 *   { empatados: [{ ca, mov, difDias }], noEmpatados: [ca], sobrantes: [mov] }
 *   - empatados: pares sistema ↔ PDF
 *   - noEmpatados: movimientos del sistema sin par en el PDF
 *   - sobrantes: movimientos del PDF sin par en el sistema
 */
export function empatarMovimientos(cargosAbonos, candidatos, opciones = {}) {
  // Por defecto 0 = sólo empata movimientos de la MISMA fecha exacta.
  const ventanaDias = opciones.ventanaDias ?? 0;
  // Empate exacto de monto con una tolerancia > 0 
  const tolerancia = opciones.tolerancia ?? 0;

  const sistema = (cargosAbonos || []).map((ca, idx) => ({
    idx,
    ca,
    monto: round2(ca.cargo != null ? ca.cargo : ca.abono),
    tipo: ca.cargo != null ? "cargo" : "abono",
    fecha: moment(ca.fechaRegistro),
    usado: false,
  }));

  const empatados = [];
  const sobrantes = [];

  (candidatos || []).forEach((mov) => {
    const montoMov = round2(mov.monto);
    let mejor = null;
    let mejorDif = Infinity;

    sistema.forEach((s) => {
      if (s.usado) return;
      if (Math.abs(s.monto - montoMov) > tolerancia) return;
      if (mov.tipo && s.tipo !== mov.tipo) return; // tipo null = comodín
      // Comparar por día de calendario: sin truncar por la hora, una diferencia
      // real de 4 días con horas distintas daría 3 con .diff("days").
      const difDias = Math.abs(
        moment(s.fecha).startOf("day").diff(moment(mov.fecha).startOf("day"), "days")
      );
      if (difDias > ventanaDias) return;
      if (difDias < mejorDif) {
        mejorDif = difDias;
        mejor = s;
      }
    });

    if (mejor) {
      mejor.usado = true;
      empatados.push({ ca: mejor.ca, mov, difDias: mejorDif });
    } else {
      sobrantes.push(mov);
    }
  });

  const noEmpatados = sistema.filter((s) => !s.usado).map((s) => s.ca);

  return { empatados, noEmpatados, sobrantes };
}
